import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { SearchableSelectOption } from '@eview/features/ui/searchable-select/searchable-select.component';
import {
  DEFAULT_ROWS_CATALOG,
  RepeatableFieldDef,
  RepeatableRowsCatalog,
  VulnerabilityRow,
  catalogFieldLabel,
  catalogOptionLabel,
  createBlankRow,
  isFieldVisible,
  parseVulnerabilityRows,
  resolveRowsCatalog,
  validateVulnerabilityRows
} from '@eview/core/domain/form/vulnerability-rows';
import {
  TITLE_TEXT_MAX_LENGTH,
  blockTitleTextKeypress,
  blockTitleTextPaste,
  hasInvalidTitleText
} from '../../validators/title-text.validation';

@Component({
  selector: 'eview-vulnerability-rows-input',
  templateUrl: './vulnerability-rows-input.component.html',
  styleUrls: ['./vulnerability-rows-input.component.scss']
})
export class VulnerabilityRowsInputComponent implements OnInit, OnChanges {
  @Input() value: string | string[] | VulnerabilityRow[] | null = null;
  @Input() required = false;
  @Input() config: any[] | null = null;
  @Output() valueChange = new EventEmitter<VulnerabilityRow[]>();

  rows: VulnerabilityRow[] = [];
  touched = false;
  errorKey: string | null = null;
  catalog: RepeatableRowsCatalog = DEFAULT_ROWS_CATALOG;
  lang = 'es';

  private skipNextValueHydrate = false;

  ngOnInit() {
    this.refreshCatalog();
    this.hydrateFromInput();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.refreshCatalog();
    }
    if (changes['value'] && !changes['value'].firstChange) {
      if (this.skipNextValueHydrate) {
        this.skipNextValueHydrate = false;
        return;
      }
      this.hydrateFromInput();
    }
  }

  trackByRowIndex(index: number): number {
    return index;
  }

  trackByFieldKey(_: number, field: RepeatableFieldDef): string {
    return field.key;
  }

  get maxRows(): number {
    return this.catalog.max_rows;
  }

  get fields(): RepeatableFieldDef[] {
    return this.catalog.fields || [];
  }

  get canAdd(): boolean {
    if (this.rows.length >= this.maxRows) {
      return false;
    }
    const uniqueField = this.fields.find(f => f.unique_across_rows && f.type === 'select');
    if (!uniqueField) {
      return true;
    }
    return this.availableOptionsFor(uniqueField, -1).length > 0;
  }

  fieldLabel(field: RepeatableFieldDef): string {
    return catalogFieldLabel(field, this.lang);
  }

  isVisible(field: RepeatableFieldDef, row: VulnerabilityRow): boolean {
    return isFieldVisible(field, row);
  }

  selectOptions(field: RepeatableFieldDef, rowIndex: number): SearchableSelectOption[] {
    return this.availableOptionsFor(field, rowIndex).map(o => ({
      value: o.value,
      label: catalogOptionLabel(o, this.lang, o.value)
    }));
  }

  availableOptionsFor(field: RepeatableFieldDef, rowIndex: number) {
    const options = field.options || [];
    if (!field.unique_across_rows) {
      return options;
    }
    const used = new Set(
      this.rows
        .map((r, i) => (i === rowIndex ? null : r[field.key]))
        .filter(v => v != null && String(v) !== '')
        .map(String)
    );
    return options.filter(o => !used.has(o.value));
  }

  readonly titleMaxLength = TITLE_TEXT_MAX_LENGTH;

  private static readonly FIELD_LEVEL_ERROR_KEYS = new Set([
    'VULN_ERR_OTHER_REQUIRED',
    'FORM_MAX_LENGTH_150',
    'FORM_NO_SPECIAL_CHARACTER'
  ]);

  isFieldLevelError(errorKey: string | null): boolean {
    return !!errorKey && VulnerabilityRowsInputComponent.FIELD_LEVEL_ERROR_KEYS.has(errorKey);
  }

  textFieldErrorKeys(field: RepeatableFieldDef, row: VulnerabilityRow): string[] {
    if (!this.touched || !this.isVisible(field, row) || field.type !== 'text') {
      return [];
    }
    const raw = String(row[field.key] ?? '');
    const keys: string[] = [];
    if (field.required !== false && !raw.trim()) {
      keys.push('VULN_ERR_OTHER_REQUIRED');
    }
    if (raw.length > TITLE_TEXT_MAX_LENGTH) {
      keys.push('FORM_MAX_LENGTH_150');
    }
    if (hasInvalidTitleText(raw)) {
      keys.push('FORM_NO_SPECIAL_CHARACTER');
    }
    return keys;
  }

  fieldHasError(field: RepeatableFieldDef, row: VulnerabilityRow): boolean {
    if (field.type === 'text') {
      return this.textFieldErrorKeys(field, row).length > 0;
    }
    if (!this.touched || !this.errorKey) {
      return false;
    }
    if (!this.isVisible(field, row)) {
      return false;
    }
    const val = String(row[field.key] ?? '').trim();
    if (field.required === false) {
      return false;
    }
    return !val;
  }

  onTitleKeypress(event: KeyboardEvent): void {
    blockTitleTextKeypress(event);
  }

  onTitlePaste(event: ClipboardEvent): void {
    blockTitleTextPaste(event);
  }

  textLength(row: VulnerabilityRow, field: RepeatableFieldDef): number {
    return String(row[field.key] ?? '').length;
  }

  addRow(): void {
    if (!this.canAdd) {
      return;
    }
    this.rows = [...this.rows, createBlankRow(this.catalog)];
    this.emitChange();
  }

  removeRow(index: number): void {
    this.rows = this.rows.filter((_, i) => i !== index);
    if (this.rows.length === 0) {
      this.rows = [createBlankRow(this.catalog)];
    }
    this.emitChange();
  }

  onSelectChange(index: number, field: RepeatableFieldDef, value: string): void {
    const copy = this.rows.map(r => ({ ...r }));
    copy[index] = { ...copy[index], [field.key]: value };
    // Clear dependent fields that are no longer visible
    for (const f of this.fields) {
      if (f.show_when?.field === field.key && !isFieldVisible(f, copy[index])) {
        copy[index][f.key] = f.type === 'text' ? null : '';
      }
    }
    this.rows = copy;
    this.emitChange();
  }

  onTextInput(index: number, field: RepeatableFieldDef, text: string): void {
    if (!this.rows[index]) {
      return;
    }
    this.rows[index][field.key] = text;
    this.emitChange();
  }

  onTextBlur(): void {
    this.touched = true;
    this.errorKey = validateVulnerabilityRows(
      this.rows,
      this.required,
      this.catalog
    );
    this.emitChange();
  }

  markAsTouched(): void {
    this.touched = true;
    this.errorKey = validateVulnerabilityRows(
      this.rows,
      this.required,
      this.catalog
    );
    this.emitChange();
  }

  private refreshCatalog(): void {
    this.lang =
      (typeof localStorage !== 'undefined' &&
        localStorage.getItem('languageCode')) ||
      'es';
    this.catalog = resolveRowsCatalog(this.config);
  }

  private hydrateFromInput(): void {
    const parsed = parseVulnerabilityRows(this.value).map(r => ({ ...r }));
    if (parsed.length > 0) {
      this.rows = parsed;
      return;
    }
    if (this.rows.length === 0) {
      this.rows = [createBlankRow(this.catalog)];
    }
  }

  private emitChange(): void {
    this.errorKey = this.touched
      ? validateVulnerabilityRows(this.rows, this.required, this.catalog)
      : null;
    this.skipNextValueHydrate = true;
    this.valueChange.emit(this.rows.map(r => ({ ...r })));
  }
}
